// src/pages/PersonalBelongings/PersonalBelongingPrint.js
import React, { useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useParams } from "react-router-dom";
import { fetchPersonalBelonging } from "../../../store/slices/personalBelongingsSlice";
import PrintWrapper, { printFooterStyles } from "../../common/PrintWrapper";
import logo from "../../../assets/images/logo-samplee.png";

export default function PersonalBelongingPrint() {
  const { id } = useParams();
  const dispatch = useDispatch();

  const { currentBelonging, isLoading, error } = useSelector(
    (state) => state.personalBelongings
  );

  useEffect(() => {
    if (id) dispatch(fetchPersonalBelonging(id));
  }, [dispatch, id]);

  useEffect(() => {
    if (currentBelonging) {
      const timer = setTimeout(() => window.print(), 300);
      window.onafterprint = () => window.close();
      return () => clearTimeout(timer);
    }
  }, [currentBelonging]);

  if (isLoading) return <div className="p-3">Loading...</div>;
  if (error) return <div className="p-3 text-danger">{error}</div>;
  if (!currentBelonging) return <div className="p-3">No data found</div>;

  const data = currentBelonging;

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return " ";
    return new Date(dateString).toLocaleDateString("en-GB");
  };

  return (
    <>
      {/* Unified Print Styles - Matches All Other Forms */}
      <style jsx global>{`
        * {
          text-align: left !important;
          box-sizing: border-box !important;
        }

        body {
          font-family: Arial, sans-serif !important;
          line-height: 1.6 !important;
          color: #000000 !important;
          background-color: #ffffffff !important;
          margin: 0 !important;
          padding: 0 !important;
        }

        .print-container {
          display: block !important;
          width: 100% !important;
          padding: 20px !important;
          position: relative !important;
        }
        ${printFooterStyles}

        /* Logo */
        .logo {
          position: absolute !important;
          top: 10px !important;
          left: 10px !important;
          z-index: 1000 !important;
        }

        .logo img {
          height: 60px !important;
          width: auto !important;
        }

        /* Header */

        .header h3 {
          font-size: 1.5em !important;
          font-weight: bold !important;
          text-align: center !important;
          border-bottom: 1.5px solid #383838 !important;
          padding-bottom: 15px !important;
          margin-bottom: 10px !important;
          color: #000000 !important;
        }

        
        .header h6 {
          font-size: 1.2em !important;
          text-align: center !important;
          margin-bottom: 5px !important;
          font-weight: semibold !important;
          color: #000000 !important;
        }

        .header p {
          font-size: 0.75em !important;
          text-align: center !important;
          margin-top: 8px !important;
          font-style: italic !important;
        }


        /* Tables */
        table {
          width: 100% !important;
          border-collapse: collapse !important;
          margin-bottom: 20px !important;
          font-size: 0.95em !important;
        }

        td {
          border: 1px solid #000 !important;
          padding: 8px 10px !important;
        }

        .items-table td {
          border: none !important;
          padding: 4px 0 !important;
          font-weight: bold !important;
          // border-bottom: 1px solid #777777ff !important;
        }

        .value {
          width: 30%;
        }

        /* Print Optimization */
        @media print {
          .print-container {
            padding: 15px !important;
          }
        }
      `}</style>

      <PrintWrapper>
        <div className="print-container font-sans">
          <div className="logo">
            <img src={logo} alt="Logo" />
          </div>
        <div className="header">
          <h3>THE RECOVERY HOUSE</h3>
          <h6>PERSONAL BELONGINGS FORM</h6>
        </div>

        <br />
        <br />
        {/* Client & Caregiver Info Table */}
        <table>
          <tbody>
            <tr>
              <td className="label">Client Name</td>
              <td className="value">
                <strong>{data.client_name || " "}</strong>
              </td>
              <td className="label">Relation with Client</td>
              <td className="value">
                <strong>{data.relation || " "}</strong>
              </td>
            </tr>
          </tbody>
        </table>

        {/* Personal Belongings - Proper Bordered Table */}
        <div className="section" style={{ marginBottom: '10px' }}>Belongings List</div>
        <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '30px' }}>
          <thead>
            <tr>
              <td className="label center" style={{ width: '10%', textAlign: 'center', fontWeight: 'bold' }}>#</td>
              <td className="label" style={{ width: '90%', fontWeight: 'bold' }}>Belongings Name / Description</td>
            </tr>
          </thead>
          <tbody>
            {Array.isArray(data.items) && data.items.length > 0 ? (
              <>
                {/* Render Actual Data */}
                {data.items.map((item, index) => (
                  <tr key={item.id || index}>
                    <td style={{ textAlign: 'center' }}>{index + 1}</td>
                    <td style={{ height: '30px' }}>{item.name || ""}</td>
                  </tr>
                ))}
                {/* If items are less than 10, fill remaining with blank rows for consistent look */}
                {data.items.length < 10 && Array.from({ length: 10 - data.items.length }).map((_, i) => (
                  <tr key={`blank-${i}`}>
                    <td style={{ textAlign: 'center' }}>{data.items.length + i + 1}</td>
                    <td style={{ height: '30px' }}>&nbsp;</td>
                  </tr>
                ))}
              </>
            ) : (
              /* Default 10 blank rows if no data exists */
              Array.from({ length: 10 }, (_, i) => (
                <tr key={`empty-${i}`}>
                  <td style={{ textAlign: 'center' }}>{i + 1}</td>
                  <td style={{ height: '30px' }}>&nbsp;</td>
                </tr>
              ))
            )}
          </tbody>
        </table>

        {/* Signatures Table */}
        <table>
          <tbody>
            {/* Relative Row */}
            <tr>
              <td>Relative Name</td>
              <td>
                <strong>{data.relative_name || " "}</strong>
              </td>
              <td colSpan={2} className="sign-col">
                <div className="sign-label">Signature</div>
              </td>
              <td className="value"></td>
            </tr>

            {/* TRH Staff Row */}
            <tr>
              <td>TRH Staff Name</td>
              <td>
                <strong>{data.trh_staff_name || " "}</strong>
              </td>
              <td colSpan={2} className="sign-col">
                <div className="sign-label">Signature</div>
              </td>
              <td className="value"></td>
            </tr>
          </tbody>
        </table>
      </div>
      </PrintWrapper>
    </>
  );
}